/* eslint class-methods-use-this: 0 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { uniqBy, isEqual } from 'lodash';
import classnames from 'classnames';
import DatePicker from 'components/v2/DatePicker';
import moment from 'moment-timezone';
import { titleize } from 'underscore.string';
import { withTranslation } from 'react-i18next';
import { getTimezones } from 'utils/Timezones';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import Alert from 'components/ui/Alert';
import LoadingCircle from 'components/ui/LoadingCircle';
import { showToast } from 'stores/ToastStore';
import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import EquipmentStore from 'stores/EquipmentStore';
import { isDrayageShipment, isIntermodalShipmentType, isExportShipment, isDrayageOrRailShipment, } from 'helpers/shipment-types';
import { requiresReasonCapture } from 'helpers/shipments';
import SelectReasonCodes from 'components/modules/marketplace-config/SelectReasonCodes';
import { validateChassis } from 'utils/ChassisAPIUtils';
import { getShipment, updateShipmentStopEquipment, updateShipment, updateSealNumberOnStopReferenceNumbers, } from 'utils/ShipmentWebAPIUtils';
import { processDateTimeChange } from 'utils/formatters/Date';
import RequiredStopDocumentsList from 'components/modules/shipments/stops/RequiredStopDocumentsList';
import { getActionDate, getIntentRequirements, isChassisMissing, stopRequiresChassis, } from 'helpers/stops';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { isFutureDate } from 'common/helpers/dateUtils';
import SoftAlertHoldNotification from 'components/modules/shipments/stops/SoftAlertHoldNotification';
import TimePicker from 'components/v2/TimePicker';
import MissingReferenceNumbers from 'components/v2/MissingReferenceNumbers';
import EquipmentTable from '../EquipmentTable';
import CargomaticModal from '../../../v2/modal';
import { PodUploader } from '../../documents/PodUploader';
import { sluggify } from '../../../../utils/localization/utils';
const SIGNATORY_MIN_LENGTH = 3;
const CHASSIS_ID_VALID_LENGTH = 10;
const SEAL_NUMBER_MIN_LENGTH = 3;
const SEAL_NUMBER_MAX_LENGTH = 10;
const getDefaultMetaState = () => ({
    signatory: '',
    date: moment(),
    time: null,
    timezone: null,
    reason: null,
    reasonCode: '',
    reasonDescription: '',
});
function validateReasonCapture({ shipper, stop, meta, t }) {
    const error = {};
    if (!requiresReasonCapture(shipper, stop, meta.date)) {
        return error;
    }
    const { reasonCode, date } = meta;
    if (!reasonCode || reasonCode === '') {
        error.value = t('SELECT_REASON_CODE');
    }
    if (!date) {
        error.value = t('ENTER_COMPLETION_DATE');
    }
    return error;
}
function validate(props, state) {
    const { files, meta: { date, signatory, reason, reasonCode }, validDateEntered, validTimeEntered, commentRequired, comment, chassis, stopRequiresChassis, missingChassis, invalidChassis, requireCodes, shipment, shipment: { shipper }, stop, stop: { action, empty, missing_docs }, intentRequirements: { pocOptional, dateRequired }, dateChanged, timeChanged, wasPreviouslyCompleted, sealNumber, } = state;
    const { isCarrier, t } = props;
    const error = Object.assign({}, validateReasonCapture({ shipper, stop, meta: { reasonCode, date }, t }));
    const isDrayage = isIntermodalShipmentType(shipment);
    if (stopRequiresChassis && missingChassis) {
        error.chassis = t('USE_BUTTON_FOR_ADD_CHASSIS_ERROR');
    }
    if (stopRequiresChassis && invalidChassis) {
        error.chassis = t('USE_VALID_CHASSIS_NUMBER');
    }
    if (stopRequiresChassis &&
        missingChassis &&
        action === 'pickup' &&
        chassis === '') {
        error.chassis = t('ADD_CHASSIS_NBR_TO_COMPLETE_STOP');
    }
    if (stopRequiresChassis &&
        missingChassis &&
        action === 'pickup' &&
        chassis.length &&
        chassis.length !== 10) {
        error.chassis = t('CHASSIS_NBR_10_DIGIT_ERROR');
    }
    const fileMissing = files.length === 0;
    if (!pocOptional && fileMissing) {
        // SSR - Remove drayage case from conditional when SSR is live
        if (!isDrayage ||
            (!featureEnabled(CONFIGURED_FEATURES.SSR) &&
                isDrayage &&
                action === 'deliver' &&
                !empty)) {
            error.file = t('UPLOAD_COMPLETION_PROOF_ERROR');
        }
    }
    if (isCarrier && !isDrayage && !files.length) {
        if (action === 'deliver') {
            error.file = t('UPLOAD_COMPLETION_PROOF_ERROR');
        }
    }
    // SSR - Remove case when SSR is live
    if (!featureEnabled(CONFIGURED_FEATURES.SSR) &&
        !files.length &&
        missing_docs) {
        error.file = t('UPLOAD_COMPLETION_PROOF_ERROR');
    }
    // date is required if a POC is provided
    if ((files.length ||
        dateRequired ||
        (!dateChanged && !wasPreviouslyCompleted)) &&
        !validDateEntered) {
        error.date = t('ENTER_COMPLETION_DATE');
    }
    if ((validDateEntered && !validTimeEntered) ||
        (!timeChanged && !wasPreviouslyCompleted)) {
        error.time = t('ENTER_COMPLETION_DATE');
    }
    if (isCarrier && signatory === '' && !files.length) {
        error.signatory = t('ENTER_SIGNATORY');
    }
    if (!pocOptional && !signatory) {
        if (!isDrayage || (isDrayage && action === 'deliver' && !empty)) {
            error.signatory = t('ENTER_SIGNATORY');
        }
    }
    else if (signatory && signatory.length < SIGNATORY_MIN_LENGTH) {
        error.signatory = t('MIN_LENGTH_CHARS_SIGNATORY', {
            signatoryMinimumLength: SIGNATORY_MIN_LENGTH,
        });
    }
    if (requireCodes) {
        if (!reason) {
            error.custom_reason_code = t('REASON_CODES_REQUIRED_TO_COMPLETE_STOP');
        }
        if (commentRequired && !comment.length) {
            error.custom_reason_code = t('COMMENT_REQUIRED_FOR_REASON_CODE');
        }
    }
    if (isExportShipment(shipment) &&
        !wasPreviouslyCompleted &&
        (action === 'pickup' || action === 'deliver') &&
        !empty &&
        (!sealNumber || sealNumber === '')) {
        error.sealNumber = t('SEAL_NUMBER_MISSING');
    }
    if (sealNumber &&
        (sealNumber.length < SEAL_NUMBER_MIN_LENGTH ||
            sealNumber.length > SEAL_NUMBER_MAX_LENGTH)) {
        error.sealNumber = t('SEAL_NUMBER_CHARACTER_COUNT');
    }
    return error;
}
function getInitialState() {
    return {
        files: [],
        meta: getDefaultMetaState(),
        error: {},
        completeDisabled: false,
        uploading: false,
        validDateEntered: true,
        validTimeEntered: false,
        isDateInvalid: false,
        uploadedPods: 0,
        uploadPodFailures: 0,
        validPods: 0,
        comment: '',
        commentRequired: false,
        requireCodes: false,
        missingChassis: false,
        equipments: [],
        chassis: '',
        invalidChassis: false,
        stopRequiresChassis: false,
        selectedChassis: null,
        invalidPdfs: [],
        reasonCodeStarted: false, // temporary reason code fix, remove later
        addedMissingReferenceNumbers: {},
        currentStopDocumentState: [],
    };
}
class StopCompleteModal extends Component {
    constructor(props) {
        super(props);
        this.fetchShipment = async () => {
            var _a, _b, _c, _d, _f, _g, _h, _j, _k;
            const { shipmentId, stopId, isCarrier } = this.props;
            try {
                const { entity: { item: shipment }, } = await getShipment(shipmentId);
                const stop = shipment.stops.find((s) => s._id === stopId);
                const completeDisabled = (stop === null || stop === void 0 ? void 0 : stop.missing_docs) || false;
                const equipments = uniqBy((stop.equipments || []).map((eq) => (Object.assign(Object.assign({}, eq), { canRemove: false }))), (eq) => eq._id);
                const intentRequirements = getIntentRequirements(shipment, stop, !isCarrier);
                const missingChassis = isChassisMissing({
                    shipment,
                    equipments,
                    isCarrier,
                });
                let availableEquipment;
                if (missingChassis) {
                    availableEquipment = uniqBy((((_a = shipment === null || shipment === void 0 ? void 0 : shipment.order) === null || _a === void 0 ? void 0 : _a.equipments) || [])
                        .filter((eq) => eq._equipmentType === 'chassis')
                        .map((eq) => (Object.assign(Object.assign({}, eq), { canAdd: true }))), (eq) => eq._id);
                }
                else {
                    availableEquipment = [];
                }
                const { date, timezone } = getActionDate(stop);
                const wasPreviouslyCompleted = !!((_d = (_c = (_b = stop === null || stop === void 0 ? void 0 : stop.timeline) === null || _b === void 0 ? void 0 : _b.complete) === null || _c === void 0 ? void 0 : _c.meta) === null || _d === void 0 ? void 0 : _d.date);
                const reasonCode = ((_h = (_g = (_f = stop === null || stop === void 0 ? void 0 : stop.timeline) === null || _f === void 0 ? void 0 : _f.complete) === null || _g === void 0 ? void 0 : _g.meta) === null || _h === void 0 ? void 0 : _h.reasonCode) || '';
                const time = date ? date.format('hh:mm A') : null;
                const currentSealNumber = (_k = (_j = stop.reference_numbers) === null || _j === void 0 ? void 0 : _j.find((x) => x.name === 'seal_number')) === null || _k === void 0 ? void 0 : _k.value;
                if (currentSealNumber) {
                    this.setState({
                        initialSealNumber: currentSealNumber,
                        sealNumber: currentSealNumber,
                    });
                }
                this.setState((state) => {
                    return {
                        completeDisabled,
                        missingChassis: isChassisMissing({
                            shipment,
                            equipments,
                            isCarrier,
                        }),
                        stopRequiresChassis: stopRequiresChassis({ shipment, isCarrier }),
                        shipment,
                        availableEquipment,
                        stop,
                        wasPreviouslyCompleted,
                        equipments,
                        validDateEntered: true,
                        validTimeEntered: true,
                        meta: Object.assign(Object.assign({}, state.meta), { reasonCode, date: date, time, timezone: timezone }),
                        loaded: true,
                        intentRequirements,
                    };
                }, () => {
                    this.validate();
                });
            }
            catch (e) {
                console.error(e);
            }
        };
        // Apply Chassis to all stops with matching containers
        this.getCreatedEquipment = () => {
            // Get newly created chassis
            const equipment = EquipmentStore.getCreatedEquipment();
            if (!equipment || equipment._equipmentType !== 'chassis') {
                return;
            }
            const equipmentAdded = this.state.equipments.find((eq) => eq.equipment_id === equipment.equipment_id);
            if (equipmentAdded) {
                return;
            }
            this.setState((prevState) => {
                const { error } = prevState;
                delete error.chassis;
                return {
                    equipments: [
                        ...prevState.equipments,
                        Object.assign(Object.assign({}, equipment), { canRemove: false }),
                    ],
                    missingChassis: false,
                    invalidChassis: false,
                    loading: false,
                    error,
                };
            }, () => {
                this.validate();
                this.updateStopEquipment(equipment);
            });
        };
        this.getCreateEquipmentError = () => {
            const error = EquipmentStore.getCreateEquipmentError();
            if (error && sluggify(error.message) === 'EQUIPMENT_MISMATCH') {
                showToast(this.props.t('EQUIPMENT_MISMATCH'), this.props.t('EQUIPMENT_MISMATCH_ERROR_MSG', {
                    equipmentId: error.existing_equipment.equipment_id,
                    equipmentType: error.existing_equipment._equipmentType,
                }), 'error');
                this.setState((prevState) => {
                    return {
                        error: Object.assign(Object.assign({}, prevState.error), { chassis: this.props.t('CHASSIS_EQUIPMENT_MISMATCH') }),
                    };
                });
            }
        };
        this.setCodesRequired = (requireCodes) => {
            this.setState({ requireCodes });
        };
        this.handleReasonSelect = (item) => {
            const code = item === null || item === void 0 ? void 0 : item.code;
            const description = item === null || item === void 0 ? void 0 : item.description;
            this.setState((state) => {
                const meta = Object.assign(Object.assign({}, state.meta), { reason: item, reasonDescription: description, reasonCode: code });
                return {
                    meta,
                    reasonCodeStarted: code && description, // temporary reason code fix, remove later
                };
            }, () => this.validate());
        };
        this.handleCommentInput = (comment) => {
            this.setState({ comment }, () => this.validate());
        };
        this.handleCommentRequired = (value) => {
            this.setState({ commentRequired: value }, () => this.validate());
        };
        this.handleSignatoryChange = (signatory) => {
            this.setState((state) => {
                const meta = Object.assign(Object.assign({}, state.meta), { signatory });
                return { meta };
            }, () => this.validate());
        };
        this.handleTimeChange = (e) => {
            const { target: { value: time }, } = e;
            this.setState((prevState) => {
                const result = processDateTimeChange({
                    date: prevState.meta.date,
                    time,
                    timezone: prevState.meta.timezone,
                });
                const { date, isDateInvalid, validTimeEntered, validDateEntered, usingFutureDate, } = result;
                const meta = Object.assign(Object.assign({}, prevState.meta), { date,
                    time });
                return {
                    meta,
                    validDateEntered,
                    validTimeEntered,
                    isDateInvalid,
                    usingFutureDate,
                    timeChanged: true,
                };
            }, () => this.validate());
        };
        this.handleDateChange = (incomingDate) => {
            this.setState((prevState) => {
                const result = processDateTimeChange({
                    date: incomingDate,
                    time: prevState.meta.time,
                    timezone: prevState.meta.timezone,
                });
                const { date, isDateInvalid, validTimeEntered, validDateEntered, usingFutureDate, } = result;
                const meta = Object.assign(Object.assign({}, prevState.meta), { date });
                return {
                    meta,
                    validDateEntered,
                    validTimeEntered,
                    isDateInvalid,
                    usingFutureDate,
                    dateChanged: true,
                };
            }, () => this.validate());
        };
        this.handleReferenceNumberChange = (refNumName, refNumValue) => {
            this.setState((state) => {
                var _a;
                const incomingReferenceNumber = {
                    name: refNumName,
                    value: refNumValue,
                    custom: false,
                    editable: true,
                    optimized: (_a = refNumValue === null || refNumValue === void 0 ? void 0 : refNumValue.toUpperCase()) === null || _a === void 0 ? void 0 : _a.trim(),
                };
                const addedMissingReferenceNumbers = Object.assign(Object.assign({}, state.addedMissingReferenceNumbers), { [refNumName]: incomingReferenceNumber });
                return { addedMissingReferenceNumbers };
            }, () => this.validate());
        };
        this.handleUpdateShipmentReferenceNumbers = async () => {
            const { addedMissingReferenceNumbers, shipment } = this.state;
            try {
                if (Object.keys(addedMissingReferenceNumbers).length) {
                    const refNumsToAdd = Object.keys(addedMissingReferenceNumbers).map((key) => addedMissingReferenceNumbers[key]);
                    await updateShipment(shipment, {
                        reference_numbers: [...shipment.reference_numbers, ...refNumsToAdd],
                    });
                }
            }
            catch (err) {
                console.error('Error while updating shipment', err);
            }
        };
        this.completeAfterAllChecks = async (marketRateData) => {
            const { commentRequired, comment, equipments, meta: currentMeta, stop, shipment, } = this.state;
            const meta = __rest(currentMeta, []);
            if (isDrayageShipment(shipment)) {
                const container = equipments.find((eq) => eq._equipmentType === 'container');
                if (container) {
                    meta.containerId = container.equipment_id;
                }
                const chassis = equipments.find((eq) => eq._equipmentType === 'chassis');
                if (chassis) {
                    meta.chassis = chassis.equipment_id;
                }
                const trailer = equipments.find((eq) => eq._equipmentType === 'trailer');
                if (trailer) {
                    meta.trailerId = trailer.equipment_id;
                }
            }
            meta.date = moment(meta.date).toISOString();
            if (commentRequired && comment && comment.length) {
                meta.message = comment;
            }
            const pods = this.state.files;
            if (pods.length) {
                meta.files = pods.map((document) => {
                    return {
                        key: document.key,
                        id: document.id,
                        bucket: document.bucket,
                        type: document.type,
                    };
                });
            }
            if (this.state.selectedChassis) {
                await this.updateStopEquipment(this.state.selectedChassis);
            }
            let yard;
            if (stop.yardId && marketRateData) {
                yard = this.props.yards.find((y) => y.id === stop.yardId).nameInternal;
                // rate and freedays comes from getMarketShipperRate or response from updateMarketShipperRate
                const { rate, freeDays } = marketRateData;
                meta.metering = {
                    yard,
                    rate,
                    freeDays,
                };
            }
            this.props.onStopComplete(this.state.shipment, stop, [], meta);
            this.onHide();
        };
        this.handleComplete = async () => {
            var _a, _b;
            const { shipment, sealNumber, stop, initialSealNumber } = this.state;
            const { marketShipperRate } = this.props;
            const error = validate(this.props, this.state);
            if (Object.keys(error).length > 0) {
                return this.setState({ error });
            }
            // rates are coming from shipment page itself
            await this.completeAfterAllChecks(marketShipperRate);
            if (isIntermodalShipmentType(shipment)) {
                await this.handleUpdateShipmentReferenceNumbers();
            }
            if (sealNumber && sealNumber !== '' && sealNumber !== initialSealNumber) {
                try {
                    await updateSealNumberOnStopReferenceNumbers((_a = shipment === null || shipment === void 0 ? void 0 : shipment._id) === null || _a === void 0 ? void 0 : _a.toString(), (_b = stop === null || stop === void 0 ? void 0 : stop._id) === null || _b === void 0 ? void 0 : _b.toString(), sealNumber);
                }
                catch (_err) {
                    console.error('Failed to Update Seal Number');
                }
            }
        };
        this.handleChassisChange = (chassis) => {
            this.setState({ chassis }, () => this.validate());
        };
        this.handleSealNumberChange = (sealNumber) => {
            this.setState({ sealNumber: sealNumber }, () => this.validate());
        };
        this.addChassis = async () => {
            const { chassis } = this.state;
            try {
                const results = await validateChassis(chassis);
                const { validated } = results;
                if (validated) {
                    const equipment = {
                        equipment_id: chassis.trim(),
                        _equipmentType: 'chassis',
                        _equipmentSize: '',
                        _type: '',
                        status: 'N/A',
                    };
                    this.setState({ loading: false });
                    return EquipmentActionCreators.addEquipment(equipment);
                }
                return this.setState({ invalidChassis: true }, this.validate);
            }
            catch (_e) {
                this.setState((state) => {
                    const { error } = state;
                    error.chassis = this.props.t('ERROR_VALIDATING_CHASSIS');
                    return { error };
                });
            }
        };
        this.onHide = () => {
            this.setState(getInitialState());
            this.props.onHide();
        };
        this.handleTimezoneChange = (zone) => {
            const timezone = zone.value;
            this.setState((prevState) => {
                const result = processDateTimeChange({
                    date: prevState.meta.date,
                    time: prevState.meta.time,
                    timezone,
                });
                const { date, isDateInvalid, validTimeEntered, validDateEntered, usingFutureDate, } = result;
                const meta = Object.assign(Object.assign({}, prevState.meta), { date,
                    timezone });
                return {
                    meta,
                    validDateEntered,
                    validTimeEntered,
                    isDateInvalid,
                    usingFutureDate,
                };
            }, () => this.validate());
        };
        this.refreshStopDataOnFileUploaded = (uploadedFiles) => {
            try {
                this.setState((currentState) => {
                    return Object.assign(Object.assign({}, currentState), { files: currentState.files.concat(uploadedFiles) });
                }, this.fetchShipment);
            }
            catch (e) {
                console.error(`Error refreshing stop data: ${e.message}`);
            }
        };
        this.updateCurrentStopDocumentsState = (currentStopDocumentState) => {
            this.setState({ currentStopDocumentState }, this.validate);
        };
        this.renderDocumentUploader = () => {
            const { t } = this.props;
            const { shipment, stop, meta, intentRequirements, files, error, shipperStopDocumentRequirements, } = this.state;
            if (shipment &&
                stop &&
                featureEnabled(CONFIGURED_FEATURES.SSR) &&
                isIntermodalShipmentType(shipment)) {
                return (React.createElement(RequiredStopDocumentsList, { shipment: shipment, stop: stop, shipperStopDocumentRequirements: shipperStopDocumentRequirements, onFileUploaded: this.refreshStopDataOnFileUploaded, onStopDocumentStateUpdate: this.updateCurrentStopDocumentsState }));
            }
            return (React.createElement("div", { "data-testid": "div-72dc30f5", className: "flex flex-col" },
                React.createElement("p", { "data-testid": "p-37903541", className: "text-sm mb-2" }, t('POC_DOCUMENT_LOCATION')),
                React.createElement(PodUploader, { shipment: { id: shipment._id }, stop: stop, meta: meta, intentRequirements: intentRequirements, error: error, onProcessing: (processing) => {
                        this.setState({
                            uploading: processing,
                        });
                    }, onUpload: (shipment, stop, type, uploadedFile) => {
                        const prevFiles = files || [];
                        this.setState({
                            files: [...prevFiles, uploadedFile],
                        }, this.validate);
                    } })));
        };
        this.state = Object.assign(Object.assign({}, getInitialState()), { loaded: false, shipperStopDocumentRequirements: props.shipperStopDocumentRequirements, dateChanged: false, timeChanged: false });
    }
    isPODRequired() {
        if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
            // When SSR goes live, we can deprecate this isPODRequired function because stops should be able to enter provide documentation state
            return false;
        }
        const { stop, shipment } = this.state;
        if (stop && shipment) {
            return (stop.missing_docs &&
                stop.action === 'deliver' &&
                (this.state.meta.signatory === '' || !this.state.meta.signatory) &&
                (this.state.files.length === 0 || !this.state.files));
        }
        return false;
    }
    async componentDidMount() {
        await this.fetchShipment();
        EquipmentStore.addChangeListener(this.getCreatedEquipment);
        EquipmentStore.addChangeListener(this.getCreateEquipmentError);
    }
    componentWillUnmount() {
        EquipmentStore.removeChangeListener(this.getCreatedEquipment);
        EquipmentStore.removeChangeListener(this.getCreateEquipmentError);
    }
    UNSAFE_componentWillReceiveProps(props) {
        if (!this.props.show &&
            props.show &&
            props.shipmentId &&
            props.stopId &&
            props.shipmentId !== this.props.shipmentId &&
            props.stopId !== this.props.stopId) {
            this.fetchShipment();
        }
        if (featureEnabled(CONFIGURED_FEATURES.SSR) &&
            (props === null || props === void 0 ? void 0 : props.shipperStopDocumentRequirements) &&
            !isEqual(props.shipperStopDocumentRequirements, this.props.shipperStopDocumentRequirements)) {
            this.setState({
                shipperStopDocumentRequirements: props.shipperStopDocumentRequirements,
            });
        }
    }
    validate() {
        const { meta: { date, timezone }, } = this.state;
        const usingFutureDate = isFutureDate(date, timezone);
        this.setState((state) => {
            const error = validate(this.props, state);
            let completeDisabled = Object.keys(error).length > 0;
            if (this.isPODRequired() || usingFutureDate) {
                completeDisabled = true;
            }
            return { error, completeDisabled, usingFutureDate };
        });
    }
    async updateStopEquipment(equipment) {
        if (equipment._equipmentType !== 'chassis') {
            return;
        }
        const { isCarrier } = this.props;
        const { shipment, stop } = this.state;
        const { receivables, payables } = shipment;
        const updates = {
            equipments: uniqBy([...this.state.equipments], (eq) => eq._id),
        };
        if (!this.props.isCarrier) {
            updates.receivables = receivables;
            updates.payables = payables;
        }
        try {
            const { entity: { job }, } = await updateShipmentStopEquipment(shipment, stop, updates, isCarrier);
            const container = (stop.equipments || []).find((eq) => eq._equipmentType === 'container');
            if (container) {
                const stopsWithSameContainer = shipment.stops.filter((selectedStop) => {
                    if (selectedStop._id === stop._id) {
                        return false;
                    }
                    const stopEquipments = selectedStop.equipments || [];
                    const hasChassis = stopEquipments.some((eq) => eq._equipmentType === 'chassis');
                    return (!hasChassis &&
                        stopEquipments.some((eq) => eq.equipment_id === container.equipment_id));
                });
                const promiseMap = stopsWithSameContainer.map((selectedStop) => {
                    const updates = {
                        equipments: [...(selectedStop.equipments || []), equipment],
                    };
                    if (!isCarrier) {
                        updates.receivables = receivables;
                        updates.payables = payables;
                    }
                    return updateShipmentStopEquipment(shipment, selectedStop, updates, isCarrier);
                });
                if (promiseMap.length > 0) {
                    const results = await Promise.all(promiseMap);
                    const job = results[results.length - 1].entity.job;
                    this.props.onStopEquipmentUpdateSuccess({
                        job,
                    });
                }
                else {
                    this.props.onStopEquipmentUpdateSuccess({ job });
                }
            }
            else {
                this.props.onStopEquipmentUpdateSuccess({ job });
            }
        }
        catch (e) {
            this.props.onStopEquipmentUpdateFail(e);
            console.error(e);
        }
    }
    getProofOfCompletionLabel() {
        const action = this.state.stop.action;
        const { t } = this.props;
        if (action === 'pickup')
            return t('PROOF_OF_PICKUP');
        if (action === 'deliver')
            return t('PROOF_OF_DELIVERY');
        if (action === 'return')
            return t('PROOF_OF_RETURN');
        return '';
    }
    handleEquipmentRemove(equipment) {
        if (equipment._equipmentType !== 'chassis') {
            return;
        }
        const { shipment } = this.state;
        const { isCarrier } = this.props;
        this.setState((prevState) => {
            const equipments = prevState.equipments.filter((e) => e._id !== equipment._id);
            return {
                missingChassis: isChassisMissing({ shipment, equipments, isCarrier }),
                selectedChassis: null,
                equipments,
                availableEquipment: [...prevState.availableEquipment, equipment].map((eq) => (Object.assign(Object.assign({}, eq), { canAdd: true }))),
            };
        }, () => this.validate());
    }
    handleEquipmentSelect(equipment) {
        if (equipment._equipmentType !== 'chassis') {
            return;
        }
        this.setState((prevState) => {
            const equipments = uniqBy([...prevState.equipments, Object.assign(Object.assign({}, equipment), { canRemove: true })], (eq) => eq._id);
            const { shipment } = prevState;
            const { isCarrier } = this.props;
            return {
                selectedChassis: equipment,
                missingChassis: isChassisMissing({ shipment, equipments, isCarrier }),
                equipments,
                availableEquipment: prevState.availableEquipment
                    .filter((eq) => eq._id !== equipment._id)
                    .map((eq) => (Object.assign(Object.assign({}, eq), { canAdd: false }))),
            };
        }, () => this.validate());
    }
    renderSealNumber() {
        var _a;
        const { t } = this.props;
        if (this.state.wasPreviouslyCompleted)
            return null;
        if (!isDrayageOrRailShipment(this.state.shipment))
            return null;
        return (React.createElement(React.Fragment, null,
            React.createElement("hr", { "data-testid": "hr-ec565bf6", className: "my-4" }),
            React.createElement("div", { "data-testid": "div-9f7852b8", className: "mb-4" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-87418fb7", id: "seal-number", label: t('SEAL_NUMBER'), type: "text", name: "stop-complete-seal-number", value: this.state.sealNumber, placeholder: t('SEAL_NUMBER'), onChange: this.handleSealNumberChange, error: { message: (_a = this.state.error) === null || _a === void 0 ? void 0 : _a.sealNumber } }))));
    }
    // Displays equipment currently attached to stop
    renderEquipment() {
        const { t } = this.props;
        const { equipments, missingChassis, stopRequiresChassis } = this.state;
        if (equipments.length === 0 || !stopRequiresChassis) {
            return null;
        }
        const { stop: { action }, loading, } = this.state;
        if (action === 'deliver' && equipments.length === 0) {
            return null;
        }
        if (loading) {
            return React.createElement("div", { "data-testid": "div-fea6eced" }, t('UPDATING_EQUIPMENT'));
        }
        const { error, chassis } = this.state;
        const chassisPlaceHolder = t('CHASSIS_NUMBER_REQUIRED');
        return (React.createElement("div", { "data-testid": "div-a084ac5d" },
            React.createElement("hr", { "data-testid": "hr-1a7d71dc" }),
            React.createElement("h4", { "data-testid": "h4-e0197c84", className: "my-0 mb-4" }, t('EQUIPMENT')),
            missingChassis && (React.createElement("div", { "data-testid": "div-13a45677", className: "mb-4" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-0406967e", error: { message: error.chassis }, className: "mb-2", label: `${t('CHASSIS_NUMBER_LENGTH', {
                        validLength: CHASSIS_ID_VALID_LENGTH,
                    })} :`, type: "text", placeholder: chassisPlaceHolder, onChange: this.handleChassisChange, value: chassis }),
                React.createElement("button", { "data-testid": "button-e200eb3c", className: "mt-2 button button-green", onClick: () => this.setState({ loading: true }, () => this.addChassis()), disabled: chassis.length !== CHASSIS_ID_VALID_LENGTH }, t('ADD_CHASSIS')))),
            React.createElement("h4", { "data-testid": "h4-b8623817", className: "my-0 mb-2" }, t('CURRENT_EQUIPMENT')),
            React.createElement(EquipmentTable, { equipments: equipments, shipmentId: this.props.shipmentId, availableEquipment: this.state.availableEquipment, onAdd: (equipment) => this.handleEquipmentSelect(equipment), onRemove: (equipment) => this.handleEquipmentRemove(equipment) })));
    }
    renderReasonCapture() {
        if (!this.state.loaded) {
            return null;
        }
        const { shipment } = this.state;
        return (React.createElement("div", { "data-testid": "div-41e7b61b", className: "mt-3 mb-4" },
            React.createElement(SelectReasonCodes, { shipment: shipment, category: "stop_complete", updateComment: this.handleCommentInput, selectReasonCode: this.handleReasonSelect, updateCommentRequired: this.handleCommentRequired, setCodesRequired: this.setCodesRequired })));
    }
    renderCaptureDetails() {
        const { t } = this.props;
        const action = this.state.stop.action === 'pickup' ? 'pickup' : 'delivery';
        const temporaryReasonCodeCheck = validateReasonCapture({
            shipper: this.state.shipment.shipper,
            stop: this.state.stop,
            meta: {
                reasonCode: this.state.meta.reasonCode,
                date: this.state.meta.date,
            },
            t,
        });
        return (React.createElement("div", { "data-testid": "div-782d9c93" },
            React.createElement("div", { "data-testid": "div-4ea12fb9", id: "sign-section", className: "mt-4" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-3f3a890e", id: "signatory", label: t('SIGNATORY'), type: "text", value: this.state.meta.signatory, placeholder: t('SIGNATORY_NAME'), onChange: this.handleSignatoryChange, error: { message: this.state.error.signatory } })),
            (!this.state.wasPreviouslyCompleted ||
                temporaryReasonCodeCheck.value ||
                this.state.reasonCodeStarted) && (React.createElement("div", { "data-testid": "div-ca269fe1" },
                React.createElement("hr", { "data-testid": "hr-874556dd" }),
                React.createElement("h4", { "data-testid": "h4-9e30db41", className: "my-0 mb-2" },
                    titleize(t(sluggify(action))),
                    " ",
                    t('INFO_LOCAL_TIME')),
                React.createElement("p", { "data-testid": "p-a9f9ce60", className: "mb-2 text-sm" }, t('SPECIFY_TIME_DRIVER_COMPLETED')),
                React.createElement("div", { "data-testid": "div-e9dac94f", className: "flex flex-wrap items-center -mx-2" },
                    React.createElement("div", { "data-testid": "div-ba04ed4b", className: "px-2" },
                        React.createElement(DatePicker, { dataTestId: "DatePicker-573805ca", className: {
                                error: this.state.error.date,
                            }, placeholderText: t('COMPLETION_DATE'), selected: this.state.dateChanged ? this.state.meta.date : '', onChange: (selectedDate) => {
                                this.handleDateChange(selectedDate);
                            }, onChangeRaw: (e) => {
                                const { target: { value }, } = e;
                                this.handleDateChange(value);
                            }, isClearable: true })),
                    React.createElement("div", { "data-testid": "div-d3bbcda8", className: "px-2" },
                        React.createElement(TimePicker, { dataTestId: "TimePicker-d936bf9e", onChange: this.handleTimeChange, value: this.state.timeChanged ? this.state.meta.time : '', className: classnames({ error: this.state.error.time }) })),
                    React.createElement("div", { "data-testid": "div-0a20daf6", className: "px-2" },
                        React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-ec0baf63", hideLabel: true, onSelect: this.handleTimezoneChange, items: getTimezones(), value: this.state.meta.timezone, valueKey: "value", textKey: "name" }))),
                React.createElement("div", { "data-testid": "div-2b351c4b" }, this.state.usingFutureDate && this.state.dateChanged && (React.createElement("div", { "data-testid": "div-290e999a", className: "text-red text-sm mt-2" }, t('COMPLETION_DATE_FUTURE_WARNING')))),
                React.createElement("div", { "data-testid": "div-45503a00" }, this.state.isDateInvalid && (React.createElement("div", { "data-testid": "div-f21b3a33", className: "mt-2 text-red" }, t('INVALID_DATE')))),
                this.renderReasonCapture())),
            isIntermodalShipmentType(this.state.shipment) && (React.createElement(MissingReferenceNumbers, { dataTestId: "MissingReferenceNumbers-3e1c533e", shipment: this.state.shipment, stop: this.state.stop, onInputChange: this.handleReferenceNumberChange }))));
    }
    renderErrors() {
        const { error } = this.state;
        const { invalidPdfs } = this.state;
        const { t } = this.props;
        const errorKeys = Object.keys(error);
        if (errorKeys.length === 0 && invalidPdfs.length === 0) {
            return null;
        }
        return (React.createElement(Alert, { dataTestId: "Alert-e40cba14", type: "error", className: "mb-4" },
            invalidPdfs.length > 0 && (React.createElement("p", { "data-testid": "p-3c876ff8" }, t('PDF_NOT_UPLOADED_TRY_AGAIN'))),
            React.createElement("ul", { "data-testid": "ul-82b008b2" },
                invalidPdfs.map((fileName, index) => (React.createElement("li", { "data-testid": `li-cfd4cdda-${index}`, key: fileName }, fileName))),
                errorKeys.map((key, index) => (React.createElement("li", { "data-testid": `li-1a5cc607-${index}`, key: key }, error[key]))))));
    }
    renderSoftAlertForHolds() {
        const { shipment } = this.state;
        if (!shipment || !shipment.holds)
            return null;
        return React.createElement(SoftAlertHoldNotification, { shipment: shipment });
    }
    renderBody() {
        const { t } = this.props;
        if (!this.state.loaded) {
            return (React.createElement(LoadingCircle, { dataTestId: "LoadingCircle-76f8ea38", text: t('LOADING') }));
        }
        return (React.createElement("div", { "data-testid": "div-2924001d" },
            this.renderSoftAlertForHolds(),
            this.renderErrors(),
            this.renderDocumentUploader(),
            this.renderCaptureDetails(),
            this.renderSealNumber(),
            this.renderEquipment()));
    }
    renderFooter() {
        const { loaded, stop, completeDisabled, uploadedPods, uploading, dateChanged, timeChanged, wasPreviouslyCompleted, } = this.state;
        const isDelivery = stop && stop.action === 'deliver' && !stop.empty;
        const { t } = this.props;
        const shouldDisableForDate = !wasPreviouslyCompleted && (!dateChanged || !timeChanged);
        const completeButtonClass = classnames({
            button: true,
            'button-green': true,
            'stop-stopCompleteModal-uploadPOD heap': isDelivery && uploadedPods > 0,
        });
        if (!loaded) {
            return null;
        }
        return (React.createElement("div", { "data-testid": "div-08d53034", className: "flex justify-end space-x-2" },
            React.createElement("button", { "data-testid": "button-b25f812e", type: "button", className: "button button-secondary", onClick: this.onHide }, t('CANCEL')),
            React.createElement("button", { "data-testid": "button-e842cd3c", id: "completeStop", disabled: completeDisabled || uploading || shouldDisableForDate, className: completeButtonClass, type: "button", onClick: this.handleComplete }, t('COMPLETE'))));
    }
    render() {
        const { loaded } = this.state;
        const { t } = this.props;
        const title = loaded
            ? this.getProofOfCompletionLabel()
            : t('COMPLETE_STOP');
        return (React.createElement(CargomaticModal, { dataTestId: "CargomaticModal-3290452e", show: this.props.show, backdrop: "static", onHide: this.onHide, showCloseButton: true, title: title, footer: this.renderFooter() }, this.renderBody()));
    }
}
StopCompleteModal.propTypes = {
    stopId: PropTypes.string.isRequired,
    shipmentId: PropTypes.string.isRequired,
};
StopCompleteModal.propTypes = {
    show: PropTypes.bool.isRequired,
    shipmentId: PropTypes.string.isRequired,
    stopId: PropTypes.string.isRequired,
    onHide: PropTypes.func.isRequired,
    onStopComplete: PropTypes.func.isRequired,
};
export default withTranslation()(StopCompleteModal);
